import React, { useState, useEffect, useContext } from 'react';
import { 
    Box, Typography, TextField, Button, Paper, Tabs, Tab, 
    Alert, AlertTitle, Divider, CircularProgress, 
    Card, CardContent, IconButton, Grid
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import SyncIcon from '@mui/icons-material/Sync';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useTranslation } from 'react-i18next';
import config, { environment } from '../../config';
import { deriveKey, encryptData, decryptData, hashFamilyId } from '../../Utils/cryptoUtils';
import { saveLocalData, getLocalData, saveFamilySecret, getFamilySecret, clearFamilySecret } from '../../Utils/storageUtils';
import axios from 'axios';
import Budget from './Budget';
import BudgetVisuals from './BudgetVisuals';
import Events from './Events';
import TodoList from './TodoList';
import StateContext from '../../Contexts/StateContext';

const FamilyManagerHome = () => {
    const { t } = useTranslation();
    const GlobalState = useContext(StateContext);
    const [familySecret, setFamilySecret] = useState(getFamilySecret() || '');
    const [isAuthorized, setIsAuthorized] = useState(!!getFamilySecret());
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [syncStatus, setSyncStatus] = useState('synced'); // 'synced', 'syncing', 'error'
    const [secretChecking, setSecretChecking] = useState(false);
    
    const apiBase = config[environment].familySyncEndpoint;
    
    const [familyData, setFamilyData] = useState({
        budget: [],
        budgetCategories: [],
        events: [],
        todos: []
    });

    useEffect(() => {
        if (isAuthorized) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthorized]);

    const loadData = async () => {
        if (loading) return; // Megelőzzük a párhuzamos betöltést
        setLoading(true);
        setError('');
        
        // 1. Próbáljuk betölteni helyileg
        const local = getLocalData();
        if (local) {
            const key = deriveKey(familySecret);
            const decrypted = decryptData(local.data, key);
            if (decrypted) {
                // Biztosítjuk az alapértelmezett mezők meglétét
                setFamilyData(prev => ({
                    ...prev,
                    ...decrypted
                }));
            }
        }

        // 2. Próbáljunk szinkronizálni a szerverrel
        try {
            const familyId = hashFamilyId(familySecret);
            const axiosConfig = {};
            if (GlobalState.userIsLogged && GlobalState.jwtAccessToken) {
                axiosConfig.headers = { Authorization: `Bearer ${GlobalState.jwtAccessToken}` };
            }
            
            let response;
            try {
                response = await axios.get(`${apiBase}${familyId}/`, axiosConfig);
            } catch (getErr) {
                if (getErr.response && getErr.response.status === 401) {
                    response = await axios.get(`${apiBase}${familyId}/`);
                } else if (getErr.response && getErr.response.status === 404) {
                    response = { data: null };
                } else {
                    throw getErr;
                }
            }
            
            if (response && response.data && response.data.encrypted_data) {
                const key = deriveKey(familySecret);
                const decrypted = decryptData(response.data.encrypted_data, key);
                
                if (decrypted) {
                    // Itt is biztosítjuk a mezők meglétét és összefésüljük, 
                    // de a szerver az erősebb (vagy a legfrissebb timestamp kellene, 
                    // de most a szerver adatot vesszük mérvadónak)
                    setFamilyData(prev => ({
                        ...prev,
                        ...decrypted
                    }));
                    saveLocalData(response.data.encrypted_data);
                    setSyncStatus('synced');
                } else {
                    setSyncStatus('error');
                }
            } else if (response && response.data === null) {
                setSyncStatus('synced');
            }
        } catch (err) {
            setSyncStatus('error');
        } finally {
            setLoading(false);
        }
    };

    const handleAuthorize = async () => {
        if (familySecret.length < 6) {
            setError(t('FamilyManager.SecretTooShort', 'A családi titokszónak legalább 6 karakternek kell lennie.'));
            return;
        }

        setSecretChecking(true);
        setError('');
        
        // Fontos: töröljük a korábbi adatokat az új belépési kísérlet előtt
        setFamilyData({
            budget: [],
            budgetCategories: [],
            events: [],
            todos: []
        });

        try {
            const familyId = hashFamilyId(familySecret);
            const axiosConfig = {};
            if (GlobalState.userIsLogged && GlobalState.jwtAccessToken) {
                axiosConfig.headers = { Authorization: `Bearer ${GlobalState.jwtAccessToken}` };
            }

            let response;
            try {
                response = await axios.get(`${apiBase}${familyId}/`, axiosConfig);
            } catch (getErr) {
                if (getErr.response && getErr.response.status === 404) {
                    // Ha 404, akkor ez egy új család vagy még nincs a szerveren
                    response = { data: null };
                } else if (getErr.response && getErr.response.status === 401) {
                    // Ha 401 (Unauthorized), megpróbáljuk vendégként (token nélkül)
                    // Ez akkor történhet, ha a JWT lejárt a böngészőben
                    try {
                        response = await axios.get(`${apiBase}${familyId}/`);
                    } catch (retryErr) {
                        if (retryErr.response && retryErr.response.status === 404) {
                            response = { data: null };
                        } else {
                            throw retryErr;
                        }
                    }
                } else {
                    throw getErr;
                }
            }
            
            // Ha van adat a szerveren ehhez a hash-hez, megpróbáljuk dekódolni
            if (response.data && response.data.encrypted_data) {
                const key = deriveKey(familySecret);
                const decrypted = decryptData(response.data.encrypted_data, key);
                
                if (!decrypted) {
                    setError(t('FamilyManager.SecretAlreadyTaken', 'Ez a családi titokszó már foglalt, kérjük válasszon másikat!'));
                    setSecretChecking(false);
                    return;
                }

                // Ha sikerült a dekódolás, akkor ez egy érvényes titokszó egy létező családhoz
                setFamilyData(prev => ({
                    ...prev,
                    ...decrypted
                }));
                saveLocalData(response.data.encrypted_data);

                // Ha be van jelentkezve, próbáljon csatlakozni a szerveren is
                if (GlobalState.userIsLogged && GlobalState.jwtAccessToken) {
                    try {
                        await axios.post(`${apiBase}${familyId}/join/`, {}, axiosConfig);
                    } catch (joinErr) {
                        // Silent join failure
                    }
                }
            }
            
            saveFamilySecret(familySecret);
            setIsAuthorized(true);
        } catch (err) {
            setError(t('Common.SomethingWentWrong', 'Valami hiba történt.'));
        } finally {
            setSecretChecking(false);
        }
    };

    const handleSync = async (newData) => {
        const key = deriveKey(familySecret);
        const encrypted = encryptData(newData, key);
        const familyId = hashFamilyId(familySecret);

        saveLocalData(encrypted);
        
        if (!GlobalState.userIsLogged) {
            // Nem bejelentkezett felhasználó esetén token nélkül próbáljuk
            try {
                await axios.post(apiBase, {
                    family_id: familyId,
                    encrypted_data: encrypted
                });
                setSyncStatus('synced');
            } catch (err) {
                setSyncStatus('error');
            }
            return;
        }

        setSyncStatus('syncing');
        const axiosConfig = {
            headers: { Authorization: `Bearer ${GlobalState.jwtAccessToken}` }
        };
        
        try {
            await axios.post(apiBase, {
                family_id: familyId,
                encrypted_data: encrypted
            }, axiosConfig);
            setSyncStatus('synced');
        } catch (err) {
            
            // Ha 401 Unauthorized, megpróbáljuk token nélkül (pl. lejárt token)
            if (err.response && err.response.status === 401) {
                try {
                    await axios.post(apiBase, {
                        family_id: familyId,
                        encrypted_data: encrypted
                    });
                    setSyncStatus('synced');
                    return;
                } catch (retryErr) {
                    // Silent retry failure
                }
            }
            
            if (err.response && err.response.status === 403) {
                try {
                    await axios.post(`${apiBase}${familyId}/join/`, {}, axiosConfig);
                    await axios.post(apiBase, {
                        family_id: familyId,
                        encrypted_data: encrypted
                    }, axiosConfig);
                    setSyncStatus('synced');
                } catch (retryErr) {
                    setSyncStatus('error');
                }
            } else {
                setSyncStatus('error');
            }
        }
    };

    const updateFamilyData = async (key, value) => {
        setFamilyData(prev => {
            const newData = { ...prev, [key]: value };
            handleSync(newData);
            return newData;
        });
    };

    if (!isAuthorized) {
        return (
            <Box sx={{ maxWidth: 500, mx: 'auto', mt: 8, p: 3 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <SecurityIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        {t('FamilyManager.Title', 'Családmenedzser')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        {t('FamilyManager.Description', 'Biztonságos, végpontok közötti titkosítással védett családi tervező.')}
                    </Typography>
                    
                    <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                        <AlertTitle>{t('FamilyManager.ZeroKnowledgeTitle', 'Zero-Knowledge Adatvédelem')}</AlertTitle>
                        {t('FamilyManager.ZeroKnowledgeDesc', 'Az adatait egy Családi titokszó védi. Ezt a titkot a szerver sosem ismeri meg, így csak Ön és családtagjai férhetnek hozzá a tartalomhoz.')}
                    </Alert>

                    <TextField
                        fullWidth
                        type="password"
                        label={t('FamilyManager.FamilySecretLabel', 'Családi titokszó')}
                        value={familySecret}
                        onChange={(e) => setFamilySecret(e.target.value)}
                        margin="normal"
                        helperText={t('FamilyManager.SecretHelper', 'Adjon meg egy közös jelszót, amit csak a családtagok ismernek.')}
                    />
                    
                    {error && <Typography color="error" variant="caption">{error}</Typography>}
                    
                    <Button 
                        fullWidth 
                        variant="contained" 
                        color="primary" 
                        size="large" 
                        onClick={handleAuthorize}
                        sx={{ mt: 3 }}
                        startIcon={secretChecking ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                        disabled={secretChecking}
                    >
                        {t('FamilyManager.AccessButton', 'Belépés')}
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    {t('FamilyManager.Title', 'Családmenedzser')}
                </Typography>
                <Box>
                    {syncStatus === 'syncing' && <CircularProgress size={20} sx={{ mr: 1, verticalAlign: 'middle' }} />}
                    {syncStatus === 'error' && (
                        <Typography variant="caption" color="error" sx={{ mr: 1 }}>
                            {t('FamilyManager.SyncError', 'Szinkronizálási hiba!')}
                        </Typography>
                    )}
                    <IconButton onClick={loadData} disabled={loading}>
                        <SyncIcon className={loading ? 'spin' : ''} />
                    </IconButton>
                    <Button 
                        size="small" 
                        onClick={() => { 
                            clearFamilySecret(); 
                            setFamilyData({
                                budget: [],
                                budgetCategories: [],
                                events: [],
                                todos: []
                            });
                            setIsAuthorized(false); 
                        }}
                    >
                        {t('FamilyManager.Logout', 'Kilépés')}
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)} 
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label={t('FamilyManager.Budget.Title', 'Költségvetés')} />
                    <Tab label={t('FamilyManager.Budget.VisualsTitle', 'Kimutatások')} />
                    <Tab label={t('FamilyManager.Events.Title', 'Események')} />
                    <Tab label={t('FamilyManager.Todos.Title', 'Teendők')} />
                </Tabs>
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ minHeight: 400 }}>
                        {tabValue === 0 && (
                            <Budget 
                                data={familyData.budget} 
                                categories={familyData.budgetCategories}
                                updateData={(v) => updateFamilyData('budget', v)} 
                                updateCategories={(v) => updateFamilyData('budgetCategories', v)}
                                exchangeRates={GlobalState.exchangeRates}
                            />
                        )}
                        {tabValue === 1 && (
                            <BudgetVisuals 
                                data={familyData.budget} 
                                categories={familyData.budgetCategories}
                                exchangeRates={GlobalState.exchangeRates}
                            />
                        )}
                        {tabValue === 2 && <Events data={familyData.events} updateData={(v) => updateFamilyData('events', v)} />}
                        {tabValue === 3 && <TodoList data={familyData.todos} updateData={(v) => updateFamilyData('todos', v)} />}
                    </Box>
                )}
            </Paper>

            <Card variant="outlined" sx={{ mt: 4, bgcolor: 'grey.50' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <QrCodeIcon sx={{ fontSize: 40, mr: 2, color: 'text.secondary' }} />
                    <Box>
                        <Typography variant="subtitle2">
                            {t('FamilyManager.InviteTitle', 'Tagok meghívása')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t('FamilyManager.InviteDesc', 'Ossza meg a Családi titokszót a családtagjaival, hogy ők is hozzáférjenek a közös adatokhoz.')}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default FamilyManagerHome;
